import { PhysicianUtilizationTableTotalsData } from '../../../types/Analytics';
import {
  DEFAULT_PACU_STEP_NAMES,
  PacuOptionalProcedureSteps,
  PacuStepNames,
} from '../../../hooks/usePacuOptionalProcedureSteps';

export type PacuJourneyMilestone = 'pacuIn' | 'chair' | 'pt' | 'ptComplete' | 'pacuOut';

const PACU_JOURNEY_MILESTONES: PacuJourneyMilestone[] = ['pacuIn', 'chair', 'pt', 'ptComplete', 'pacuOut'];

const milestoneLabel = (id: PacuJourneyMilestone, names: PacuStepNames): string => {
  switch (id) {
    case 'pacuIn':
      return 'PACU In';
    case 'chair':
      return names.transferredToChair;
    case 'pt':
      return names.transferredToPT;
    case 'ptComplete':
      return names.ptComplete;
    case 'pacuOut':
      return 'PACU Out';
  }
};

const pacuJourneyMetricKeys: Record<
  string,
  { avg: keyof PhysicianUtilizationTableTotalsData; med: keyof PhysicianUtilizationTableTotalsData }
> = {
  'pacuIn->chair': { avg: 'pacuInToTransferToChairAvg', med: 'pacuInToTransferToChairMed' },
  'pacuIn->pt': { avg: 'pacuInToTransferToPTAvg', med: 'pacuInToTransferToPTMed' },
  'pacuIn->ptComplete': { avg: 'pacuInToPTCompleteAvg', med: 'pacuInToPTCompleteMed' },
  'pacuIn->pacuOut': { avg: 'recoveryAvg', med: 'recoveryMed' },
  'chair->pt': { avg: 'transferToChairToTransferToPTAvg', med: 'transferToChairToTransferToPTMed' },
  'chair->ptComplete': { avg: 'transferToChairToPTCompleteAvg', med: 'transferToChairToPTCompleteMed' },
  'chair->pacuOut': { avg: 'transferToChairToPacuOutAvg', med: 'transferToChairToPacuOutMed' },
  'pt->ptComplete': { avg: 'transferToPTToPTCompleteAvg', med: 'transferToPTToPTCompleteMed' },
  'pt->pacuOut': { avg: 'transferToPTToPacuOutAvg', med: 'transferToPTToPacuOutMed' },
  'ptComplete->pacuOut': { avg: 'ptCompleteToPacuOutAvg', med: 'ptCompleteToPacuOutMed' },
};

export type PacuJourneySlice = {
  name: string;
  value: number;
  from: PacuJourneyMilestone;
  to: PacuJourneyMilestone;
};

export type PacuJourneyRing = {
  name: string;
  value: number;
  /** Leading inner slices this ring covers. */
  sliceCount: number;
};

/** Consecutive slices between enabled PACU milestones. Disabled steps are skipped. */
export const pacuJourneyMetrics = (
  steps: PacuOptionalProcedureSteps,
  totals: PhysicianUtilizationTableTotalsData | undefined,
  aggregation: string
): PacuJourneySlice[] => {
  const names = steps.names ?? DEFAULT_PACU_STEP_NAMES;
  const enabled: Record<PacuJourneyMilestone, boolean> = {
    pacuIn: true,
    chair: steps.transferredToChair,
    pt: steps.transferredToPT,
    ptComplete: steps.ptComplete,
    pacuOut: true,
  };
  const milestones = PACU_JOURNEY_MILESTONES.filter(id => enabled[id]).map(id => ({
    id,
    label: milestoneLabel(id, names),
  }));

  return milestones.slice(0, -1).map((from, index) => {
    const to = milestones[index + 1];
    const keys = pacuJourneyMetricKeys[`${from.id}->${to.id}`];
    const raw = keys ? (aggregation === 'average' ? totals?.[keys.avg] : totals?.[keys.med]) : 0;
    const value = typeof raw === 'number' && isFinite(raw) ? raw : 0;
    return { name: `${from.label} to ${to.label}`, value, from: from.id, to: to.id };
  });
};

/**
 * Orange outer ring for PACU In through the transferred-to-PT timestamp.
 * That span is the sum of PACU In → chair and chair → PT.
 * If one of those slices is absent, the ring covers only the slice that remains.
 */
export const pacuJourneyOuterRing = (
  slices: PacuJourneySlice[],
  names: PacuStepNames = DEFAULT_PACU_STEP_NAMES
): PacuJourneyRing | null => {
  const ptIndex = slices.findIndex(slice => slice.to === 'pt');
  const endIndex = ptIndex >= 0 ? ptIndex : slices.findIndex(slice => slice.to === 'chair');
  if (endIndex < 0) {
    return null;
  }
  const covered = slices.slice(0, endIndex + 1);
  const value = covered.reduce((sum, slice) => sum + slice.value, 0);
  if (value <= 0) {
    return null;
  }
  const endsAtPt = covered[covered.length - 1].to === 'pt';
  const endName = endsAtPt ? names.transferredToPT : names.transferredToChair;
  return {
    name: `PACU In to ${endName}`,
    value,
    sliceCount: covered.length,
  };
};
